
import { KeenIcon, DefaultTooltip, LazyFormikSelect, DatePicker, CustomOption, CustomSingleValue } from '@/components';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage, getIn } from "formik";
import { Link, useNavigate, useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'sonner';
import { useContactOptions, useLazyOptions } from '@/hooks';
import { useAbility } from '@/providers/';
import { Can } from '@casl/react';
import expenseService from '@/services/billing/expenses/expenseService';
import matterService from '@/services/matter/matterService';
import categoryService from '@/services/billing/expenses/categoryService';
import contactTypeService from '@/services/contact/contactTypeService';
import userService from '@/services/setting/userService';
import { CategoryModal } from '@/pages/billing';
import flatFeesService from '@/services/billing/flatFeesService';
import itemService from '@/services/billing/itemService';
import { ItemsModal } from '@/pages/billing';
import { Container } from '@/components/container';
import invoiceService from '@/services/billing/invoice/invoiceService';
import { formatDateToYMD, calculateSubtotal } from '@/utils';
import { useUserOptions } from '@/hooks'

const validationSchema = Yup.object().shape({
    contact_id: Yup.string()
        .required('Contact is required'),
    matter_id: Yup.string()
        .required('Matter is required'),
    item_id: Yup.string()
        .required('Item is required'),
    date: Yup.date()
        .required('Date is required')
        .typeError('Invalid date format'),
    date_to: Yup.date()
        .transform((value, original) => (original === '' || original === null ? null : value))
        .nullable()
        .min(Yup.ref('date'), 'The end date must be on or after the start date')
        .typeError('Invalid date format'),
    quantity: Yup.number()
        .required('Quantity is required')
        .min(1, 'Quantity must be at least 1')
        .typeError('Quantity must be a number'),
    price: Yup.number()
        .required('Price is required')
        .min(0, 'Price cannot be negative')
        .typeError('Price must be a number'),
});

const EditFlatFees = () => {
    const [dataLoading, setDataLoading] = useState(false)
    const [flatFeeData, setFlatFeeData] = useState([]);
    const [userData, setuserData] = useState([]);
    const [matterData, setMatterData] = useState([])
    const [itemData, setItemData] = useState([])
    const [createContactLoading, setCreateContactLoading] = useState(false)
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create');
    const { id } = useParams();
    const flatFeeId = id;
    const Navigate = useNavigate()
    const formikRef = useRef();
    const [itemId, setItemId] = useState(null)
    const [selectedData, setSelectedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [taxesData, setTaxesData] = useState([])
    const { ability } = useAbility();
    const [formValues, setFormValues] = useState({});
    const loadUserOptions = useUserOptions({ defaultPageSize: 20 });


    const handleSubmit = async (formData, resetForm, setErrors) => {
        if (flatFeeId) {
            setCreateContactLoading(true)
            try {
                const { data, status } = await flatFeesService.updateFlatFees(formData, flatFeeId);
                if (status) {
                    // resetForm()
                    toast.success('Flat Fee updated.', {
                        description: `Flat Fee Updated Successfully.`,
                        action: {
                            label: 'Ok',
                        },
                    });
                    Navigate('/billing/flat-fees')
                }
            } catch (error) {
                console.error('Error', error)
                if (error.message == '422 Unprocessable Entity. Validation failed.') {
                    toast.warning('Validation Error.', {
                        description: 'Please check your input. Some fields may be invalid.',
                        action: {
                            label: 'Ok',
                        },
                    });
                    return
                }
                toast.error('Oops! Something went wrong.', {
                    description: 'An error occurred while updating Flat Fees. Please try again in a moment.',
                    action: {
                        label: 'Ok',
                    },
                });
            } finally {
                setCreateContactLoading(false);
            }
        }
    };

    const handleSubmitItem = async (formData) => {
        setIsLoading(true)
        try {
            const { data, status } = await itemService.createItem(formData);
            if (status) {
                setOpen(false)
                getAllItems()
                toast.success('Item Created Successfully.', {
                    description: `${data.name} item Created Successfully.`,
                });
            }
        } catch (error) {
            console.error('Error', error)
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                const validationErrors = error.errors
                const formattedErrors = Object.fromEntries(
                    Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
                );

                formikRef.current?.setErrors(formattedErrors);
                toast.error('Validation Error.', {
                    description: 'Please check your input. Some fields may be invalid.',
                    action: {
                        label: 'Ok',
                    },
                });
            }
        } finally {
            setIsLoading(false)
        }
    };

    const getSingleFlatFee = async (flatFeeId) => {
        setDataLoading(true)
        try {
            const { data, status } = await flatFeesService.getSingleFlatFees(flatFeeId);
            if (status) {
                setFlatFeeData(data)
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Flat Fee.', {
                description: 'An error occurred while fetching flat fee. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const loadContactOptions = useContactOptions({ defaultPageSize: 20 });

    const getAllMatters = async () => {
        setDataLoading(true)
        try {
            const { data, status } = await matterService.getAllMatters();
            if (status) {
                const Data = data.map(matter => ({
                    label: matter.name,
                    value: matter.id.toString(),
                    id: matter.id.toString(),
                }));
                setMatterData(Data)
                return Data
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Matters.', {
                description: 'An error occurred while fetching matters. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }
    const getAllMattersRelatedToContact = async (contactId, index) => {
        console.log('id', contactId)
        setDataLoading(true)
        try {
            const { data, status } = await invoiceService.getContactMatterRelatedData('contact', contactId);
            if (status) {
                return data.matters.map(m => ({
                    label: m.name,
                    value: m.id.toString(),
                    id: m.id.toString(),
                }));
            }
            return [];
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Matters.', {
                description: 'An error occurred while fetching matters. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const {
        data: matterOptions,
        loading: matterLoading,
        load: loadMatters,
        reset: resetMatters,
    } = useLazyOptions(async (contactId) => {
        if (!contactId) {
            return
            // await getAllMatters(); 
        } else {
            return await getAllMattersRelatedToContact(contactId);
        }
    });

    const getAllItems = async () => {
        setDataLoading(true)
        try {
            const { data, status } = await itemService.getAllItem();
            if (status) {
                const Data = data.map(item => ({
                    label: item.name,
                    value: item.id.toString(),
                    id: item.id.toString(),
                    rate: item.rate
                }));
                setItemData(Data)
                return Data
            }
        } catch (error) {
            console.log(error)
            toast.error('Failed to fetch Expense Items.', {
                description: 'An error occurred while fetching expense items. Please try again later.',

            });
        } finally {
            setDataLoading(false)
        }
    }
    const {
        data: itemOptions,
        loading: itemLoading,
        load: loadItems
    } = useLazyOptions(getAllItems);

    const handleModalClose = () => {
        setItemId(null)
        setSelectedData(null)
        setMode('create')
        setOpen(false)
    }

    const handleSubmitCategory = async (formData) => {
        setIsLoading(true)
        try {
            const { data, status } = await categoryService.createCategory(formData);
            if (status) {
                setOpen(false)
                // getAllContactType()
                toast.success('Category Created Successfully.', {
                    description: `${data.category_name} Category Created Successfully.`,
                    action: {
                        label: 'Ok',
                    },
                });
            }
        } catch (error) {
            console.error('Error', error)
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                toast.warning('Validation Error.', {
                    description: 'Please check your input. Some fields may be invalid.',
                });
            }
            toast.error('Failed to Create Category.', {
                description: 'An error occurred while creating category. Please try again later.',
            });
        } finally {
            setIsLoading(false)
        }
    };

    useEffect(() => {
        if (flatFeeId) { getSingleFlatFee(flatFeeId); }
    }, [])

    const initialValues = {
        contact_id: flatFeeData?.contact_id || '',
        contact: flatFeeData?.contact
            ? {
                label: `${flatFeeData.contact.first_name} ${flatFeeData.contact.last_name}`,
                value: flatFeeData.contact.id,
                id: flatFeeData.contact.id
            }
            : null,
        matter_id: flatFeeData?.matter_id || '',
        matter: flatFeeData?.matter
            ? {
                label: flatFeeData.matter.name,
                value: flatFeeData.matter.id,
                id: flatFeeData.matter.id
            }
            : null,
        item_id: flatFeeData?.item_id || '',
        item: flatFeeData?.item
            ? {
                label: flatFeeData.item.name,
                value: flatFeeData.item.id,
                id: flatFeeData.item.id
            }
            : null,
        date: formatDateToYMD(flatFeeData?.date || ''),
        date_to: formatDateToYMD(flatFeeData?.date_to || ''),
        quantity: flatFeeData?.quantity || 1,
        rate: flatFeeData?.rate || '',
        billed_by: flatFeeData?.billed_by?.id || '',
        billed_by_user: flatFeeData?.billed_by
            ? {
                label: `${flatFeeData.billed_by.first_name} ${flatFeeData.billed_by.last_name}`,
                value: flatFeeData.billed_by.id,
                id: flatFeeData.billed_by.id,
                email: flatFeeData.billed_by.email
            } : null,
        status: flatFeeData?.status || 'billable',
        description: flatFeeData?.description || '',
        note: flatFeeData?.note || '',
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { resetForm, setErrors }) => {
                    // return console.log("Form Submitted:", payload); 
                    handleSubmit(values, resetForm, setErrors);
                }}
            >
                {({ formik, errors, values, handleChange, handleSubmit, setFieldValue, setValues, setTouched, setErrors }) => {

                    return (
                        <Form onSubmit={handleSubmit} className='px-2.5'>
                            <div className="card pb-2.5">
                                <div className="card-header">
                                    <h3 className="card-title">Edit Flat Fee</h3>
                                </div>
                                <div className="grid gap-5 card-body">
                                    <div className="grid gap-5 xs:grid-cols-3">
                                        <div>
                                            <label className="mb-1 form-label">Contact <span className='pl-px text-red-600'>*</span></label>
                                            <LazyFormikSelect
                                                name={`contact_id`}
                                                placeholder="Select Contact"
                                                loadOptions={loadContactOptions}
                                                pageSize={20}
                                                preloadedOption={initialValues.contact}
                                                onOptionSelect={(selected) => {
                                                    if (selected?.id) {
                                                        loadMatters(selected.id, true);
                                                    }
                                                }}
                                            />
                                            <ErrorMessage
                                                name={`contact_id`}
                                                component="div"
                                                className="mt-1 text-xs text-red-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Matter <span className='pl-px text-red-600'>*</span></label>
                                            <LazyFormikSelect
                                                name={`matter_id`}
                                                options={matterOptions}
                                                placeholder="Select Matter"
                                                onFocus={() => {
                                                    const contactId = getIn(values, `contact_id`);
                                                    loadMatters(contactId || null);
                                                }}
                                                isLoading={matterLoading}
                                                preloadedOption={initialValues.matter}
                                            />
                                            <ErrorMessage name={`matter_id`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Item <span className='pl-px text-red-600'>*</span></label>
                                            <LazyFormikSelect
                                                name={`item_id`}
                                                options={itemOptions}
                                                placeholder="Select an item"
                                                onFocus={() => loadItems(null)}
                                                isLoading={itemLoading}
                                                noOptionsMessage={() => (
                                                    isLoading ? (
                                                        <span className="text-sm">Loading...</span>
                                                    ) : (
                                                        <div>
                                                            <span className="mr-1 text-sm">No options available</span>
                                                            <Can I='handle' a='item' ability={ability}>
                                                                <button type="button"
                                                                    onClick={() => setOpen(true)}
                                                                    className="text-sm text-blue-500 underline"
                                                                >
                                                                    Add new
                                                                </button>
                                                            </Can>
                                                        </div>
                                                    )
                                                )}
                                                preloadedOption={initialValues.item}
                                                onOptionSelect={(selected) => {
                                                    if (selected?.id) {
                                                        setFieldValue(`rate`, selected.rate);
                                                    }
                                                }}
                                            />
                                            <ErrorMessage name={`item_id`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div className='w-full'>
                                            <label className="mb-1 form-label">Billed By</label>
                                            <LazyFormikSelect
                                                name={`billed_by`}
                                                placeholder="Select Assignee"
                                                loadOptions={loadUserOptions}
                                                pageSize={20}
                                                preloadedOption={initialValues.billed_by_user}
                                                components={{
                                                    Option: CustomOption,
                                                    SingleValue: CustomSingleValue,
                                                }}
                                            />
                                            <ErrorMessage name={`billed_by`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Quantity</label>
                                            <Field className="input" type="text" name={`quantity`} />
                                            <ErrorMessage name={`quantity`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Price <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name={`price`} />
                                            <ErrorMessage name={`price`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-5">
                                        <div className=''>
                                            <label className="mb-1 form-label">Total</label>
                                            <div className='input'>
                                                {calculateSubtotal(values.rate, values.quantity)}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Date <span className='pl-px text-red-600'>*</span></label>
                                            <Field name={`date`}>
                                                {({ field, form, meta }) => (
                                                    <div>
                                                        <DatePicker field={field} form={form} isRange={false} />
                                                        {meta.touched && meta.error && (
                                                            <div className="mt-1 text-xs text-red-500">{meta.error}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </Field>
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">To <span className='text-[11px]'>(optional)</span></label>
                                            <Field name={`date_to`}>
                                                {({ field, form, meta }) => (
                                                    <div>
                                                        <DatePicker field={field} form={form} isRange={false} />
                                                        {meta.touched && meta.error && (
                                                            <div className="mt-1 text-xs text-red-500">{meta.error}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </Field>
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Status</label>
                                            <Field as="select" className="input" name={`status`}>
                                                <option value="billable">Billable</option>
                                                <option value="billed">Billed</option>
                                                <option value="paid">Paid</option>
                                                <option value="not billable">Not Billable</option>

                                            </Field>
                                        </div>
                                    </div>

                                    <div className='grid grid-cols-2 gap-5'>
                                        <div>
                                            <label className="mb-1 form-label">Description</label>
                                            <Field as="textarea" className="input pt-2 h-[120px]" name="description" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Note</label>
                                            <Field as="textarea" className="input pt-2 h-[120px]" name="note" />
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="inline-flex items-center justify-end gap-2">
                                        <button type="submit" disabled={createContactLoading} className="btn btn-primary">
                                            {createContactLoading ? 'Please Wait...' : 'Update Flat Fee'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>

            <ItemsModal
                open={open}
                onCloseModal={handleModalClose}
                handleMode={mode}
                data={selectedData}
                onSubmit={handleSubmitItem}
                createFieldLoading={isLoading}
                updateFieldLoading={loading}
                taxesData={taxesData}
                ref={formikRef}
            />
        </>
    );
};
export { EditFlatFees };
